import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { createEntityCollectionStore, memoizeSelector, pipeState } from '@core/ngrx-legacy';
import type { ApproverDecision, RequisitionAssignedFlow } from '@core/requisitions-types';

import type { AssignedFlowEntity } from './assigned-flows.types';
import { getAssignedFlowEntityId } from './assigned-flows.types';

const updateApproverDecision = (
  flow: RequisitionAssignedFlow,
  approverDecision: Partial<ApproverDecision> & { id: number },
): RequisitionAssignedFlow => {
  flow.steps = flow.steps.map(step => ({
    ...step,
    approverDecisions: step.approverDecisions?.map(decision => {
      if (decision.id && decision.id === approverDecision.id) {
        return {
          ...decision,
          ...approverDecision,
        };
      } else return { ...decision };
    }),
  }));
  return flow;
};

const { namespace, reducer, actions, selectors } =
  createEntityCollectionStore<AssignedFlowEntity>()('RequisitionsAssignedFlows', {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => ({
      fetch: (state, payload: { requisitionId: number }) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId(payload)],
        }),
      fetchSuccess: (
        state,
        payload: { requisitionId: number; data: RequisitionAssignedFlow | null },
      ) =>
        pipeState(
          state,
          next =>
            adapter.upsertOne(
              next,
              {
                id: getAssignedFlowEntityId({
                  requisitionId: payload.requisitionId,
                }),
                approvalFlow: payload.data,
              },
              { scope: 'main' },
            ),
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [
                getAssignedFlowEntityId({
                  requisitionId: payload.requisitionId,
                }),
              ],
            }),
        ),
      fetchFailed: (state, payload: { requisitionId: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
      sendReminder: (state, payload: { requisitionId: number; approverDecisionId: number }) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
        }),
      sendReminderSuccess: (
        state,
        payload: {
          requisitionId: number;
          approverDecision: Partial<ApproverDecision> & { id: number };
        },
      ) =>
        pipeState(
          state,
          next => {
            const id = getAssignedFlowEntityId({ requisitionId: payload.requisitionId });
            let flow = { ...next.entityMap[id]?.approvalFlow! };
            flow = updateApproverDecision(flow, payload.approverDecision);

            return adapter.updateOne(next, { id, approvalFlow: flow });
          },
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
            }),
        ),
      sendReminderFailed: (state, payload: { requisitionId: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
      updateApprovers: (
        state,
        payload: { requisitionId: number; stepId: number; approverIds: number[] },
      ) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
        }),
      updateApproversSuccess: (
        state,
        payload: { requisitionId: number; approvalFlow: RequisitionAssignedFlow },
      ) =>
        pipeState(
          state,
          next =>
            adapter.updateOne(next, {
              id: getAssignedFlowEntityId({ requisitionId: payload.requisitionId }),
              approvalFlow: payload.approvalFlow,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
            }),
        ),
      updateApproversFailed: (
        state,
        payload: { requisitionId: number; error: HttpErrorResponse },
      ) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
      deleteApprover: (
        state,
        payload: { requisitionId: number; stepId: number; approverId: number },
      ) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
        }),
      deleteApproverSuccess: (
        state,
        payload: { requisitionId: number; approvalFlow: RequisitionAssignedFlow },
      ) =>
        pipeState(
          state,
          next =>
            adapter.updateOne(next, {
              id: getAssignedFlowEntityId({ requisitionId: payload.requisitionId }),
              approvalFlow: payload.approvalFlow,
            }),
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
            }),
        ),
      deleteApproverFailed: (state, payload: { requisitionId: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
      approveRequest: (state, payload: { requisitionId: number; approverDecisionId: number }) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
        }),
      approveRequestSuccess: (
        state,
        payload: {
          requisitionId: number;
          approverDecision: Partial<ApproverDecision> & { id: number };
        },
      ) =>
        pipeState(
          state,
          next => {
            const id = getAssignedFlowEntityId({ requisitionId: payload.requisitionId });
            let flow = { ...next.entityMap[id]?.approvalFlow! };
            flow = updateApproverDecision(flow, payload.approverDecision);

            return adapter.updateOne(next, { id, approvalFlow: flow });
          },
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
            }),
        ),
      approveRequestFailed: (state, payload: { requisitionId: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
      rejectRequest: (
        state,
        payload: { requisitionId: number; approverDecisionId: number; reason: string },
      ) =>
        adapter.markAsPending(state, {
          scope: 'main',
          entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
        }),
      rejectRequestSuccess: (
        state,
        payload: {
          requisitionId: number;
          approverDecision: Partial<ApproverDecision> & { id: number };
        },
      ) =>
        pipeState(
          state,
          next => {
            const id = getAssignedFlowEntityId({ requisitionId: payload.requisitionId });
            let flow = { ...next.entityMap[id]?.approvalFlow! };
            flow = updateApproverDecision(flow, payload.approverDecision);

            return adapter.updateOne(next, { id, approvalFlow: flow });
          },
          next =>
            adapter.markAsLoaded(next, {
              scope: 'main',
              entities: [getAssignedFlowEntityId({ requisitionId: payload.requisitionId })],
            }),
        ),
      rejectRequestFailed: (state, payload: { requisitionId: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, {
          scope: 'main',
          entities: [
            getAssignedFlowEntityId({
              requisitionId: payload.requisitionId,
            }),
          ],
        }),
    }),
  });

type SelectorProps = { requisitionId: number };

const byRequisitionId = memoizeSelector((props: SelectorProps) =>
  createSelector(
    selectors.byId(getAssignedFlowEntityId(props)),
    state => state?.approvalFlow || null,
  ),
);

const isLoaded = memoizeSelector((props: SelectorProps) =>
  createSelector(selectors.isLoaded(getAssignedFlowEntityId(props)), state => state),
);

const AssignedFlowsActions = {
  fetch: actions.fetch,
  fetchSuccess: actions.fetchSuccess,
  fetchFailed: actions.fetchFailed,
  sendReminder: actions.sendReminder,
  sendReminderSuccess: actions.sendReminderSuccess,
  sendReminderFailed: actions.sendReminderFailed,
  updateApprovers: actions.updateApprovers,
  updateApproversSuccess: actions.updateApproversSuccess,
  updateApproversFailed: actions.updateApproversFailed,
  deleteApprover: actions.deleteApprover,
  deleteApproverSuccess: actions.deleteApproverSuccess,
  deleteApproverFailed: actions.deleteApproverFailed,
  approveRequest: actions.approveRequest,
  approveRequestSuccess: actions.approveRequestSuccess,
  approveRequestFailed: actions.approveRequestFailed,
  rejectRequest: actions.rejectRequest,
  rejectRequestSuccess: actions.rejectRequestSuccess,
  rejectRequestFailed: actions.rejectRequestFailed,
};

const AssignedFlowsSelectors = {
  byRequisitionId,
  isLoaded,
};

export {
  AssignedFlowsActions,
  AssignedFlowsSelectors,
  namespace as AssignedFlowsNamespace,
  reducer as AssignedFlowsReducer,
};
